import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, Params } from '@angular/router';
import _ from 'lodash';
import { map } from 'rxjs';

import { GraphQLService } from 'src/app/services/graph-ql.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { ConfirmModalComponent } from 'src/app/shared/components/confirm-modal/confirm-modal.component';
import { DateFilterListComponent } from 'src/app/shared/components/date-filter-list/date-filter-list.component';
import { FilterListComponent } from 'src/app/shared/components/filter-list/filter-list.component';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { PhotographerQueries } from 'src/app/shared/types/get-query';
import { Paginable } from 'src/app/shared/types/paginable';
import { Selectable } from 'src/app/shared/types/selectable.type';
import { Photographer, PhotographerFilters, PhotographerStatus } from '../../models/new/photographer';
import { Status } from '../../models/new/status';
import { PhotographerMapper } from '../../services/photographer-mapper';
import { DateRange } from '../../models/new/dateRange';
import { Brand } from '../../models/new/brand.model';
import { BrandFilterService } from 'src/app/services/filters/brand-filter.service';
import { PhotographerStatusFilterService } from 'src/app/services/filters/photographer-status-filter.service';
import { StringFilterListComponent } from 'src/app/shared/components/string-filter-list/string-filter-list.component';
import { MutationQuery } from 'src/app/shared/types/mutation-query.enum';

@Component({
	standalone: false,
	selector: 'bt-company-photographer-list',
	templateUrl: './company-photographer-list.component.html',
	styleUrls: ['./company-photographer-list.component.scss']
})
export class CompanyPhotographerListComponent {
	photographers: Paginable<Photographer> = new Paginable<Photographer>();
	photographerFilters = new PhotographerFilters();
	photographerStatus = PhotographerStatus;
	loading: boolean = false;

	constructor(
		private graphQlService: GraphQLService,
		private dialog: MatDialog,
		private route: ActivatedRoute,
		private utilitiesService: UtilitiesService,
		private photographerMapper: PhotographerMapper,
		private router: Router,
		private brandFilterService: BrandFilterService,
		private photographerStatusFilterService: PhotographerStatusFilterService
	) {}

	ngOnInit(): void {
		this.getPhotographers();
	}

	prepareFilterOptions(params: Params) {
		//questi chiamate servono per popolare il nome delle tendine di parametri in input tramite url
		if (params['statuses'])
			this.photographerStatusFilterService.searchById(params['statuses']).subscribe((res: Status[]) => {
				this.photographerFilters.statuses?.forEach((status) => {
					const found = res.find((s) => s.id == status.id);
					status.name = found ? found.name : '';
				});
			});

		if (params['brands'])
			this.brandFilterService.searchById(params['brands']).subscribe((res: Brand[]) => {
				this.photographerFilters.brands?.forEach((brand) => {
					const found = res.find((b) => b.id == brand.id);
					brand.name = found ? found.name : '';
				});
			});
	}

	goToPhotographerOrders(id: number) {
		this.router.navigate(['/company/order-list'], {
			queryParams: {
				photographers: [id]
			}
		});
	}

	getPhotographers() {
		this.loading = true;
		this.route.queryParams.subscribe((params: Params) => {
			this.photographerFilters = this.photographerMapper.urlToFilters(params);

			//metodo che popola le label dei filtri in base agli id passati da url,
			this.prepareFilterOptions(params);

			this.photographers.currentPage = this.photographerFilters.page || 1;
			this.photographers.size = this.photographerFilters.size || 2;

			const pagination = this.photographers.buildQuery(this.photographers.currentPage);

			this.graphQlService
				.getData<any>(PhotographerQueries.GET_PHOTOGRAPHERS, { where: this.photographerMapper.toWhereClause(this.photographerFilters), ...pagination })
				.pipe(
					map((res: any) => {
						const photographers = res.data.photographers;

						const paginable = new Paginable<Selectable<Photographer>>({
							items: photographers.nodes.map((node: any) => ({
								...this.photographerMapper.toPhotographer(node),
								selected: false
							})),
							totalCount: photographers.totalCount,
							currentPage: this.photographers.currentPage,
							size: this.photographers.size
						});

						return paginable;
					})
				)
				.subscribe({
					next: (data: Paginable<Selectable<Photographer>>) => {
						this.photographers = data;

						this.loading = false;
					},
					error: (err) => {
						this.utilitiesService.showSnackbar('Error fetching photographers', SnackbarType.Error);
						this.loading = false;
						console.error(err);
					}
				});
		});
	}

	pageEvent(event: { pageNumber: number; pageSize: number }) {
		this.photographers.currentPage = event.pageNumber;
		this.photographers.size = event.pageSize;

		this.photographerFilters.page = event.pageNumber;
		this.photographerFilters.size = event.pageSize;

		this.refreshNavigation();
	}

	refreshNavigation() {
		this.loading = true;

		this.router.navigate([location.pathname], {
			queryParams: this.photographerMapper.filtersToUrl(this.photographerFilters)
		});
	}

	removePhotographer(photographer: Photographer) {
		this.dialog
			.open(ConfirmModalComponent, {
				data: {
					title: 'Confirm deletion',
					message: `Are you sure you want to delete photographer ${photographer.fullName}?`
				}
			})
			.afterClosed()
			.subscribe({
				next: (res) => {
					if (res && photographer.id) {
						this.loading = true;
						this.graphQlService
							.postData(MutationQuery.DELETE_PHOTOGRAPHER, {
								deletePhotographerInput: {
									personId: photographer.id
								}
							})
							.subscribe({
								next: (value: any) => {
									if (!value?.data?.deletePhotographer?.success) {
										this.loading = false;
										this.utilitiesService.showSnackbar(value?.data?.deletePhotographer?.message || 'Error during deleting photographer', SnackbarType.Error);
										return;
									}

									this.utilitiesService.showSnackbar('Photographer deleted successfully', SnackbarType.Success);
									this.getPhotographers();
								},
								error: (err) => {
									this.loading = false;
									this.utilitiesService.showSnackbar(err?.networkError?.error?.errors?.[0]?.message || 'Error during deleting photographer', SnackbarType.Error);
									console.error(err);
								}
							});
					}
				}
			});
	}

	cleanFilter(filter: string) {
		switch (filter) {
			case 'brands':
				this.photographerFilters.brands = undefined;
				break;
			case 'contractDate':
				this.photographerFilters.contractDate = undefined;
				break;

			case 'textSearch':
				this.photographerFilters.textSearch = undefined;
				break;

			case 'statuses':
				this.photographerFilters.statuses = undefined;
				break;
		}

		this.photographerFilters.page = 1;

		this.refreshNavigation();
	}

	openContractDateFilterModal() {
		this.dialog
			.open(DateFilterListComponent, {
				data: {
					title: 'Contract Date',
					selected: _.cloneDeep(this.photographerFilters.contractDate)
				},
				width: '400px'
			})
			.afterClosed()
			.subscribe({
				next: (res: DateRange) => {
					if (res) {
						this.photographerFilters.contractDate = res;
						this.refreshNavigation();
					}
				}
			});
	}

	openNameFilter() {
		this.dialog
			.open(StringFilterListComponent, {
				data: { title: 'Name or surname', selected: this.photographerFilters.textSearch }
			})
			.afterClosed()
			.subscribe({
				next: (res) => {
					if (res) {
						this.photographerFilters.textSearch = _.cloneDeep(res);

						this.refreshNavigation();
					}
				}
			});
	}

	openBrandFilterModal(): void {
		this.dialog
			.open(FilterListComponent<Brand>, {
				data: {
					title: 'Brands',
					selected: this.photographerFilters.brands
						? this.photographerFilters.brands.map((el) => ({
								...el,
								displayId: el.id,
								displayName: el.name,
								selected: true
							}))
						: [],
					filterService: this.brandFilterService
				},
				width: '400px'
			})
			.afterClosed()
			.subscribe({
				next: (res: Array<Selectable<Brand>>) => {
					if (res) {
						this.photographerFilters.brands = res.map((el: any) => new Brand({ id: el.displayId, name: el.displayName }));

						this.refreshNavigation();
					}
				}
			});
	}

	openStatusFilterModal(): void {
		const dialog = this.dialog
			.open(FilterListComponent<PhotographerStatus>, {
				data: {
					title: 'Statuses',

					selected: this.photographerFilters.statuses
						? this.photographerFilters.statuses.map((el) => ({
								...el,
								displayId: el.id,
								displayName: el.name,
								selected: true
							}))
						: [],
					filterService: this.photographerStatusFilterService
				},
				width: '400px'
			})
			.afterClosed()
			.subscribe({
				next: (res: Array<Selectable<Status>>) => {
					if (res) {
						this.photographerFilters.statuses = res.map((el: any) => new Status({ id: el.displayId, name: el.displayName }));
						this.refreshNavigation();
					}
				}
			});
	}
}
